import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function CheckoutSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('polling');  // polling | paid | expired | error
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(search).get('session_id');
    if (!sessionId) { setStatus('error'); return; }

    let cancelled = false;
    const MAX = 10;
    const pollOnce = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/api/huscleos/checkout/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        if (n < MAX) {
          setTimeout(() => { setAttempts(n + 1); pollOnce(n + 1); }, 2000);
        } else {
          setStatus('error');
        }
      } catch (err) {
        if (n < MAX) {
          setTimeout(() => { setAttempts(n + 1); pollOnce(n + 1); }, 2000);
        } else {
          setStatus('error');
        }
      }
    };
    pollOnce(0);
    return () => { cancelled = true; };
  }, [search]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-md w-full">
        <div className="bg-white border border-gray-200 p-10 text-center shadow-sm" data-testid="checkout-success-card">
          {status === 'polling' && (
            <>
              <Loader2 size={36} className="mx-auto mb-5 animate-spin text-[#FF2E00]" />
              <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-2">Confirming your payment</p>
              <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px' }}>Almost there...</h1>
              <p className="text-sm text-gray-600">Checking Stripe — this usually takes a few seconds. ({attempts + 1}/10)</p>
            </>
          )}
          {status === 'paid' && (
            <>
              <CheckCircle2 size={48} className="mx-auto mb-5 text-green-500" />
              <p className="text-xs font-bold tracking-widest uppercase text-green-600 mb-2">Payment Received</p>
              <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px' }}>Welcome to HuscleOS.</h1>
              <p className="text-sm text-gray-600 mb-2">
                You just paid <strong>${(details?.amount_total || 0) / 100}</strong> for the{' '}
                <strong>{details?.metadata?.tier?.toUpperCase()}</strong> plan.
              </p>
              <p className="text-sm text-gray-500 mb-6">Check your inbox — your welcome + onboarding email is on its way.</p>
              <Link
                to="/staff/onboarding"
                data-testid="checkout-success-onboarding-btn"
                className="inline-flex items-center gap-2 px-8 py-3 text-xs font-bold tracking-widest uppercase text-white bg-[#FF2E00] hover:opacity-90 transition-all"
              >
                VIEW ONBOARDING <ArrowRight size={14} />
              </Link>
            </>
          )}
          {status === 'expired' && (
            <>
              <XCircle size={48} className="mx-auto mb-5 text-gray-400" />
              <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Bebas Neue' }}>Session expired</h1>
              <p className="text-sm text-gray-600 mb-6">Your checkout session timed out. No charge was made.</p>
              <button onClick={() => navigate('/')} className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-white bg-gray-900 hover:opacity-90">RETURN HOME</button>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle size={48} className="mx-auto mb-5 text-red-500" />
              <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Bebas Neue' }}>Could not confirm</h1>
              <p className="text-sm text-gray-600 mb-6">We could not verify your payment status. If you completed payment, please check your email or contact us.</p>
              <Link to="/" className="px-6 py-3 text-xs font-bold tracking-widest uppercase text-white bg-gray-900 hover:opacity-90">RETURN HOME</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function CheckoutCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      <div className="max-w-md w-full">
        <div className="bg-white border border-gray-200 p-10 text-center" data-testid="checkout-cancel-card">
          <XCircle size={48} className="mx-auto mb-5 text-gray-400" />
          <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-2">Checkout Cancelled</p>
          <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Bebas Neue' }}>No charge made.</h1>
          <p className="text-sm text-gray-600 mb-6">You cancelled the checkout before completing payment. Nothing was charged.</p>
          <Link to="/#pricing" className="inline-flex items-center gap-2 px-6 py-3 text-xs font-bold tracking-widest uppercase text-white bg-[#FF2E00] hover:opacity-90">
            BACK TO PRICING <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
